import React, { useCallback, useMemo } from 'react';
import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../../common/form/task-form/model/task-form-init';
import { deserializeTaskItems } from '../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { RepeatableGroupFormikValue, RepeatableGroupInstance } from './PublicFormView';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { TaskFormFieldList } from '../../../common/form/task-form/TaskFormFieldList';

interface RepeatableGroupFieldProps {
  formikKey: string;
  groupData: RepeatableGroupFormikValue;
}

export const RepeatableGroupField: React.FC<RepeatableGroupFieldProps> = ({ formikKey, groupData }) => {
  const { setFieldValue, values: allValues } = useFormikContext<TaskFormValuesType>();
  const { theme } = useCampfireTheme();
  const { title: groupTitle, description, label, minInstances, maxInstances, groupTaskItems } = groupData;
  const title = groupTitle || label;

  const currentGroupData = (allValues as any)[formikKey] as RepeatableGroupFormikValue;
  const instances = currentGroupData?.instances || [];

  const childFieldItems = useMemo(
    () => groupTaskItems.filter((item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType'),
    [groupTaskItems]
  );

  const deserializedGroupItems = useMemo(() => deserializeTaskItems(groupTaskItems as any), [groupTaskItems]);

  const addInstance = useCallback(() => {
    if (instances.length >= maxInstances) return;
    const newInstanceValues = initTaskFormFormikValues(childFieldItems, undefined);
    const newInstance: RepeatableGroupInstance = {
      groupInstanceId: uuidv4(),
      values: newInstanceValues,
    };
    setFieldValue(formikKey, {
      ...currentGroupData,
      instances: [...instances, newInstance],
    });
  }, [instances, maxInstances, childFieldItems, setFieldValue, formikKey, currentGroupData]);

  const removeInstance = useCallback(
    (index: number) => {
      if (instances.length <= minInstances) return;
      setFieldValue(formikKey, {
        ...currentGroupData,
        instances: instances.filter((_unused: any, i: number) => i !== index),
      });
    },
    [instances, minInstances, setFieldValue, formikKey, currentGroupData]
  );

  return (
    <Box mt={3} mb={2}>
      <Box paddingLeft={1} mb={1}>
        <Typography variant='h6'>{title}</Typography>
        {description && (
          <Typography variant='body2' color='textSecondary'>
            {description}
          </Typography>
        )}
      </Box>

      {instances.map((instance: RepeatableGroupInstance, index: number) => (
        <Box
          key={instance.groupInstanceId}
          mb={2}
          style={{
            border: `1px solid ${theme.color.grey.border}`,
            borderRadius: 4,
            padding: 16,
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
            <Typography variant='body2' color='textSecondary'>
              {label} {index + 1}
            </Typography>
            {instances.length > minInstances && (
              <IconButton size='small' onClick={() => removeInstance(index)}>
                <Close fontSize='small' />
              </IconButton>
            )}
          </Box>

          <TaskFormFieldList
            taskItems={deserializedGroupItems}
            values={instance.values}
            disableAutoFocus
            fieldNamePrefix={`${formikKey}.instances.${index}.values`}
          />
        </Box>
      ))}

      {instances.length < maxInstances && (
        <Box pl={1}>
          <Grid container alignItems='center' spacing={1} style={{ cursor: 'pointer' }} onClick={addInstance}>
            <Grid item>
              <Add color='primary' fontSize='small' />
            </Grid>
            <Grid item>
              <Typography variant='body2' color='primary'>
                {instances.length === 0 ? 'Add' : 'Add another'} {label.toLowerCase()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
